import { useState, useEffect } from "react";
import { CheckCircle2, Circle, Store, CreditCard, BarChart3, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Getting Started Checklist Component
 * Shows users their setup progress: Store Connected → First Analysis → Subscription Active
 * Can be dismissed and remembers state in localStorage
 */
const GettingStartedChecklist = ({ 
  hasStore, 
  hasAnalysis, 
  hasActiveSubscription,
  onConnectStore,
  onStartCheckout 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check localStorage for dismissed state
  useEffect(() => {
    const dismissed = localStorage.getItem('replydriven_checklist_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Calculate completion status
  const steps = [
    {
      id: 'subscription',
      title: 'Start your free trial',
      description: 'Choose a plan and get 7 days free',
      completed: hasActiveSubscription,
      action: hasActiveSubscription ? null : onStartCheckout,
      actionLabel: 'Choose Plan'
    },
    {
      id: 'store',
      title: 'Connect your Shopify store',
      description: 'Secure read-only access to your store data',
      completed: hasStore,
      action: hasStore ? null : onConnectStore,
      actionLabel: 'Connect Store'
    },
    {
      id: 'analysis',
      title: 'Get your first insight',
      description: 'AI analyzes your store and finds profit leaks',
      completed: hasAnalysis,
      action: null, // This happens automatically
      actionLabel: null
    }
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const allComplete = completedCount === steps.length;
  const progressPercent = (completedCount / steps.length) * 100;

  // Don't show if dismissed or all complete
  if (isDismissed || allComplete) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem('replydriven_checklist_dismissed', 'true');
    setIsDismissed(true);
  };

  return (
    <div className="bg-gradient-to-r from-[#D4F34A]/5 to-transparent border border-[#D4F34A]/20 rounded-xl p-5 mb-6" data-testid="getting-started-checklist">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#D4F34A]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Getting Started</h3>
            <p className="text-sm text-text-muted">{completedCount} of {steps.length} steps complete</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-white/5 rounded-md transition-colors"
            data-testid="toggle-checklist"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-text-muted" />
            ) : (
              <ChevronDown className="w-4 h-4 text-text-muted" />
            )}
          </button>
          <button 
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/5 rounded-md transition-colors"
            data-testid="dismiss-checklist"
            title="Dismiss checklist"
          >
            <X className="w-4 h-4 text-text-muted" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1.5 bg-[#262626] rounded-full mb-4 overflow-hidden">
        <div 
          className="h-full bg-[#D4F34A] rounded-full transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Steps */}
      {isExpanded && (
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div 
              key={step.id}
              className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                step.completed 
                  ? 'bg-[#D4F34A]/5' 
                  : index === completedCount 
                    ? 'bg-surface border border-[#262626]' 
                    : 'opacity-50'
              }`}
              data-testid={`checklist-step-${step.id}`}
            >
              {/* Step Icon */}
              <div className="flex-shrink-0 mt-0.5">
                {step.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-[#D4F34A]" />
                ) : (
                  <Circle className="w-5 h-5 text-text-muted" />
                )}
              </div>

              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${step.completed ? 'text-text-muted line-through' : 'text-white'}`}>
                  {step.title}
                </p>
                <p className="text-sm text-text-muted">{step.description}</p>
              </div>

              {/* Action Button */}
              {!step.completed && step.action && index === completedCount && (
                <Button
                  onClick={step.action}
                  size="sm"
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-semibold flex-shrink-0"
                  data-testid={`checklist-action-${step.id}`}
                >
                  {step.actionLabel}
                </Button>
              )}

              {/* Waiting indicator for analysis step */}
              {step.id === 'analysis' && !step.completed && hasStore && hasActiveSubscription && (
                <span className="text-xs text-text-muted bg-[#262626] px-2 py-1 rounded-full flex-shrink-0">
                  Auto-runs weekly
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Completion message when expanded */}
      {isExpanded && completedCount === steps.length - 1 && hasStore && hasActiveSubscription && (
        <p className="text-sm text-[#D4F34A] mt-3 text-center">
          🎉 Almost there! Your first insight will be generated automatically.
        </p>
      )}
    </div>
  );
};

export default GettingStartedChecklist;
